import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { authFetch, API } from './api';
import { Users, UserPlus, Shield, Edit2, Key, X, Check, AlertTriangle, ToggleLeft, ToggleRight } from 'lucide-react';

export default function UserManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ full_name: '', email: '', password: '', role: 'coach', school_id: '' });
  const [schools, setSchools] = useState([]);
  const [editForm, setEditForm] = useState({ full_name: '', email: '', role: '' });
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    loadUsers();
    if (user?.role === 'super_admin') {
      authFetch(`${API}/api/schools/`).then(r => r.ok ? r.json() : []).then(setSchools);
    }
  }, []);

  const loadUsers = async () => {
    try {
      const res = await authFetch(`${API}/api/auth/users`);
      if (res.ok) setUsers(await res.json());
    } catch (err) { console.error('Failed to load users:', err); }
    finally { setLoading(false); }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    try {
      const res = await authFetch(`${API}/api/auth/register`, {
        method: 'POST',
        body: JSON.stringify({ full_name: form.full_name, email: form.email, password: form.password, role: form.role, school_id: user.role === 'super_admin' ? parseInt(form.school_id) : user.school_id })
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed to create user'); }
      setSuccess(`${form.role === 'coach' ? 'Coach' : 'Individual'} account created for ${form.email}`);
      setForm({ full_name: '', email: '', password: '', role: 'coach' });
      setShowAddForm(false);
      loadUsers();
    } catch (err) { setError(err.message); }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      const res = await authFetch(`${API}/api/auth/users/${editingUser.id}`, {
        method: 'PATCH',
        body: JSON.stringify(editForm)
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed to update user'); }
      setSuccess(`Updated ${editForm.full_name || editingUser.full_name}`);
      setEditingUser(null);
      loadUsers();
    } catch (err) { setError(err.message); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    try {
      const res = await authFetch(`${API}/api/auth/users/${resetPasswordUser.id}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ new_password: newPassword })
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed to reset password'); }
      setSuccess(`Password reset for ${resetPasswordUser.full_name}`);
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (err) { setError(err.message); }
  };

  const handleToggleActive = async (u) => {
    setError(''); setSuccess('');
    try {
      const res = await authFetch(`${API}/api/auth/users/${u.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: !u.is_active })
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed to update user'); }
      setSuccess(`${u.full_name} ${u.is_active ? 'deactivated' : 'activated'}`);
      loadUsers();
    } catch (err) { setError(err.message); }
  };

  const startEdit = (u) => {
    setEditingUser(u);
    setEditForm({ full_name: u.full_name, email: u.email, role: u.role });
    setResetPasswordUser(null);
    setShowAddForm(false);
    setError('');
  };

  const startResetPassword = (u) => {
    setResetPasswordUser(u);
    setNewPassword('');
    setEditingUser(null);
    setShowAddForm(false);
    setError('');
  };

  const getRoleBadge = (role) => {
    const styles = {
      super_admin: 'bg-purple-600/20 text-purple-300 border-purple-600/30',
      school_admin: 'bg-blue-600/20 text-blue-300 border-blue-600/30',
      coach: 'bg-green-600/20 text-green-300 border-green-600/30',
      parent: 'bg-yellow-600/20 text-yellow-300 border-yellow-600/30',
      demo: 'bg-orange-600/20 text-orange-300 border-orange-600/30',
    };
    const labels = {
      super_admin: 'Super Admin', school_admin: 'School Admin', coach: 'Coach', parent: 'Individual', demo: 'Demo',
    };
    return (
      <span className={`px-2 py-0.5 rounded border text-xs font-medium ${styles[role] || 'bg-gray-600/20 text-gray-300 border-gray-600/30'}`}>
        {labels[role] || role}
      </span>
    );
  };

  if (!user || !['super_admin', 'school_admin'].includes(user.role)) {
    return (
      <div className="min-h-screen bg-[#0A0E27] pt-12">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="bg-[#1A2744] rounded-xl p-8 text-center border border-[#546E7A]/30">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-white text-xl font-bold mb-2">Access Denied</h2>
            <p className="text-gray-400">Only administrators can manage users.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Users className="w-6 h-6 text-[#00D4FF]" />
              User Management
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              {user.school_name ? `Manage users for ${user.school_name}` : 'Manage all users'}
            </p>
          </div>
          <button
            onClick={() => { setShowAddForm(!showAddForm); setEditingUser(null); setResetPasswordUser(null); setError(''); setSuccess(''); }}
            className="flex items-center gap-2 px-4 py-2 bg-[#00D4FF] text-[#0A0E27] rounded-lg font-semibold text-sm hover:bg-[#00D4FF]/90 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            Add User
          </button>
        </div>

        {/* Success Message */}
        {success && (
          <div className="bg-green-900/30 border border-green-700/50 text-green-300 px-4 py-3 rounded-lg mb-4 text-sm flex items-center gap-2">
            <Check className="w-4 h-4" /> {success}
          </div>
        )}

        {/* Error Message */}
        {error && !showAddForm && !editingUser && !resetPasswordUser && (
          <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        {/* Add User Form */}
        {showAddForm && (
          <div className="bg-[#1A2744] rounded-xl p-6 border border-[#546E7A]/30 mb-6">
            <h3 className="text-white font-semibold mb-4">Add New User</h3>
            {error && <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>}
            <form onSubmit={handleAddUser} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Full Name</label>
                  <input type="text" value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})}
                    className="w-full px-4 py-2.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                    placeholder="Coach Smith" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                  <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                    className="w-full px-4 py-2.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                    placeholder="coach@school.edu" required />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Password</label>
                  <input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})}
                    className="w-full px-4 py-2.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                    placeholder="Minimum 8 characters" required minLength={8} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Role</label>
                  <select value={form.role} onChange={(e) => setForm({...form, role: e.target.value})}
                    className="w-full px-4 py-2.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">
                    <option value="coach">Coach — full access to your school's players</option>
                    <option value="parent">Individual — view access to a specific athlete</option>
                    {user.role === 'super_admin' && <option value="school_admin">School Admin — manages their school</option>}
                  </select>
                </div>
              </div>
              {user.role === 'super_admin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Assign to School</label>
                  <select value={form.school_id} onChange={(e) => setForm({...form, school_id: e.target.value})}
                    className="w-full px-4 py-2.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" required>
                    <option value="">Select a school...</option>
                    {schools.map(s => <option key={s.id} value={s.id}>{s.name} ({s.city}, {s.state})</option>)}
                  </select>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button type="submit" className="px-6 py-2.5 bg-[#00D4FF] text-[#0A0E27] rounded-lg font-semibold text-sm hover:bg-[#00D4FF]/90">Create Account</button>
                <button type="button" onClick={() => { setShowAddForm(false); setError(''); }} className="px-6 py-2.5 bg-gray-700 text-gray-300 rounded-lg font-semibold text-sm hover:bg-gray-600">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Edit User Form */}
        {editingUser && (
          <div className="bg-[#1A2744] rounded-xl p-6 border border-[#00D4FF]/30 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-semibold">Edit User: {editingUser.full_name}</h3>
              <button onClick={() => setEditingUser(null)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            {error && <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>}
            <form onSubmit={handleEditUser} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Full Name</label>
                  <input type="text" value={editForm.full_name} onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                    className="w-full px-4 py-2.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                  <input type="email" value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                    className="w-full px-4 py-2.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" required />
                </div>
              </div>
              <div className="w-1/2">
                <label className="block text-sm font-medium text-gray-300 mb-1">Role</label>
                <select value={editForm.role} onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                  className="w-full px-4 py-2.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">
                  {user.role === 'super_admin' && <option value="super_admin">Super Admin</option>}
                  {user.role === 'super_admin' && <option value="school_admin">School Admin</option>}
                  <option value="coach">Coach</option>
                  <option value="parent">Individual</option>
                  {user.role === 'super_admin' && <option value="demo">Demo</option>}
                </select>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button type="submit" className="px-6 py-2.5 bg-[#00D4FF] text-[#0A0E27] rounded-lg font-semibold text-sm hover:bg-[#00D4FF]/90">Save Changes</button>
                <button type="button" onClick={() => setEditingUser(null)} className="px-6 py-2.5 bg-gray-700 text-gray-300 rounded-lg font-semibold text-sm hover:bg-gray-600">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Reset Password Form */}
        {resetPasswordUser && (
          <div className="bg-[#1A2744] rounded-xl p-6 border border-yellow-600/30 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-semibold">Reset Password: {resetPasswordUser.full_name}</h3>
              <button onClick={() => setResetPasswordUser(null)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            {error && <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>}
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="w-1/2">
                <label className="block text-sm font-medium text-gray-300 mb-1">New Password</label>
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                  placeholder="Minimum 8 characters" required minLength={8} />
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button type="submit" className="px-6 py-2.5 bg-yellow-600 text-white rounded-lg font-semibold text-sm hover:bg-yellow-700">Reset Password</button>
                <button type="button" onClick={() => setResetPasswordUser(null)} className="px-6 py-2.5 bg-gray-700 text-gray-300 rounded-lg font-semibold text-sm hover:bg-gray-600">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Users List */}
        <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 overflow-hidden">
          <div className="px-6 py-4 border-b border-[#546E7A]/30">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#00D4FF]" />
              Current Users
              <span className="text-gray-400 font-normal text-sm ml-2">({users.length})</span>
            </h3>
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No users found</div>
          ) : (
            <div className="divide-y divide-[#546E7A]/20">
              {users.map((u) => (
                <div key={u.id} className="px-4 md:px-6 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 hover:bg-[#243B55]/30 transition-colors">
                  <div className="flex items-center gap-3 md:gap-4 min-w-0">
                    <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#243B55] flex items-center justify-center text-[#00D4FF] font-bold text-sm">
                      {u.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                    </div>
                    <div className="min-w-0">
                      <div className="text-white font-medium text-sm truncate">{u.full_name}</div>
                      <div className="text-gray-400 text-xs truncate">{u.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 md:gap-3 flex-wrap md:flex-nowrap pl-13 md:pl-0">
                    {u.school_name && <span className="text-gray-400 text-xs truncate max-w-[45%] md:max-w-none">{u.school_name}</span>}
                    {getRoleBadge(u.role)}
                    <div className="flex items-center gap-1 ml-auto md:ml-2">
                      <button onClick={() => startEdit(u)} title="Edit user"
                        className="p-2.5 md:p-1.5 text-gray-400 hover:text-[#00D4FF] hover:bg-[#243B55] rounded transition-colors">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => startResetPassword(u)} title="Reset password"
                        className="p-2.5 md:p-1.5 text-gray-400 hover:text-yellow-400 hover:bg-[#243B55] rounded transition-colors">
                        <Key className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleToggleActive(u)} title={u.is_active ? 'Deactivate' : 'Activate'}
                        className={`p-2.5 md:p-1.5 rounded transition-colors ${u.is_active ? 'text-green-400 hover:text-red-400 hover:bg-[#243B55]' : 'text-red-400 hover:text-green-400 hover:bg-[#243B55]'}`}>
                        {u.is_active ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
